"use client";

import { Comedian, getComedianByIDOrThrow } from "@punchup/data/legacy";
import { Dialog, DialogContent } from "../../shadcn/ui/dialog";
import { Drawer, DrawerContent } from "../../shadcn/ui/drawer";
import { useEffect, useState } from "react";

import CheckoutHeader from "./CheckoutHeader";
import { FOLLOW_LOCATIONS } from "@punchup/data/legacy";
import { SignInSignUpForm } from "../signin";
import { SpecialRow } from "@punchup/data/legacy";
import { useMediaQuery } from "@punchup/utils/hooks";
import { useSearchParams } from "next/navigation";

export interface CheckoutModalOrDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  comedianToFollow: Comedian;
  special?: SpecialRow;
  redirectTo: string;
  followLocation: FOLLOW_LOCATIONS;
  onOpen?: (params: {
    comedianId: string;
    comedianName: string | null;
    isDesktop: boolean;
    followLocation: FOLLOW_LOCATIONS;
  }) => void;
}

export default function CheckoutModalOrDrawer({
  isOpen,
  setIsOpen,
  comedianToFollow,
  special,
  redirectTo,
  followLocation,
  onOpen,
}: CheckoutModalOrDrawerProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [signatureImg, setSignatureImg] = useState<string | null>(null);
  const showWelcomeBanner = useSearchParams()?.get("showWelcomeBanner") === "1";

  useEffect(() => {
    getComedianByIDOrThrow(comedianToFollow.id).then((comedian) => {
      const signatureImg = comedian.signature_img || null;
      setSignatureImg(signatureImg);
    });
  }, [comedianToFollow.id]);

  const { slug, display_name } = comedianToFollow;

  const redirectToFromSuggested = display_name?.length
    ? `/${slug}`
    : `/confirmation/?comedian=${slug}`;

  if (!process.env.NEXT_PUBLIC_IS_EXTERNAL_SITE) {
    // Route through /suggested so the user hits the onboarding stepper, but
    // preserve the caller's intended final destination (e.g. the special page
    // they hit Subscribe for) instead of clobbering it with the comedian page.
    // Encode so inner query strings (e.g. ?autoplay=1) survive the round-trip.
    const postOnboardingTarget = redirectTo || redirectToFromSuggested;
    redirectTo = `/suggested?redirectTo=${encodeURIComponent(postOnboardingTarget)}`;
  }

  const commonContent = (
    <div className="flex flex-col items-center px-4 md:px-0">
      <CheckoutHeader
        comedian={comedianToFollow}
        special={special}
        showPreview={false}
        showWelcomeBanner={showWelcomeBanner}
      />
      <div className="pt-4 pb-12 md:pt-2 md:pb-10">
        <SignInSignUpForm
          redirectTo={redirectTo}
          buttonText="Join the Guest List"
          comedianToFollow={comedianToFollow}
          followLocation={followLocation}
          policyFooterType="comedian"
        />
      </div>
    </div>
  );

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen({
        comedianId: comedianToFollow.id,
        comedianName: comedianToFollow.display_name,
        isDesktop: isDesktop,
        followLocation,
      });
    }
  }, [isOpen, comedianToFollow, isDesktop, followLocation, onOpen]);

  if (!isDesktop) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent className="z-[99] font-regular flex items-center gap-x-4">
          {commonContent}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="rounded-lg md:w-full gap-y-0 md:max-w-md">
        {commonContent}
      </DialogContent>
    </Dialog>
  );
}
